import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { searchMedia, searchNews, formatDate } from "@/utils/media";
import MediaCard from "@/components/feature/MediaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";

type ResultType = "all" | "movie" | "tv" | "news";

const typeTabs: { value: ResultType; label: string; icon: string }[] = [
  { value: "all", label: "全部", icon: "ri-apps-2-line" },
  { value: "movie", label: "电影", icon: "ri-film-line" },
  { value: "tv", label: "电视剧", icon: "ri-tv-2-line" },
  { value: "news", label: "资讯", icon: "ri-newspaper-line" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [resultType, setResultType] = useState<ResultType>("all");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useSEO({
    title: query ? `“${query}” 搜索结果 - 麻豆精品` : "搜索 - 麻豆精品",
    description: `在麻豆精品搜索“${query}”相关的电影、电视剧与影视资讯结果。`,
    keywords: `麻豆精品,搜索,${query}`,
  });

  const mediaResults = useMemo(() => searchMedia(query), [query]);
  const newsResults = useMemo(() => searchNews(query), [query]);

  const movies = mediaResults.filter((item) => item.type === "movie");
  const shows = mediaResults.filter((item) => item.type === "tv");

  const filteredMedia =
    resultType === "movie"
      ? movies
      : resultType === "tv"
        ? shows
        : resultType === "all"
          ? mediaResults
          : [];

  const showNews = resultType === "all" || resultType === "news";
  const filteredNews = showNews ? newsResults : [];

  const totalCount = filteredMedia.length + filteredNews.length;
  const hasQuery = query.trim().length > 0;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    if (!next) {
      setParams({});
      return;
    }
    setParams({ q: next });
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "搜索" }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          搜索影视内容
        </h1>
        <p className="mt-2 text-sm text-foreground-600">输入关键词，查找电影、电视剧与影视资讯</p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center"
        role="search"
      >
        <div className="relative flex-1">
          <i
            className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"
            aria-hidden="true"
          ></i>
          <input
            type="search"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="搜索电影、电视剧、资讯…"
            className="h-12 w-full rounded-full border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <button
          type="submit"
          className="flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-7 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
        >
          <i className="ri-search-line" aria-hidden="true"></i>
          搜索
        </button>
      </form>

      {hasQuery && (
        <p className="mt-5 text-sm text-foreground-600">
          关键词「<strong className="font-semibold text-foreground-900">{query}</strong>」共找到{" "}
          <strong className="font-semibold text-primary-600">{totalCount}</strong> 个结果
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-1 rounded-full bg-background-200 p-1 sm:w-fit">
        {typeTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setResultType(tab.value)}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
              resultType === tab.value
                ? "bg-background-50 text-foreground-900"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            <i className={tab.icon} aria-hidden="true"></i>
            {tab.label}
          </button>
        ))}
      </div>

      {!hasQuery ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-search-line"
            title="开始你的搜索"
            description="输入片名、题材、地区或资讯关键词，即可快速找到相关内容。"
          />
        </div>
      ) : totalCount === 0 ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="没有找到相关结果"
            description="换个关键词试试，或浏览我们为您推荐的热门内容。"
            actionLabel="浏览最新电影"
            actionTo="/category/movie"
          />
        </div>
      ) : (
        <div className="mt-8 space-y-10">
          {filteredMedia.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-movie-2-line text-primary-600" aria-hidden="true"></i>
                影视结果（{filteredMedia.length}）
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
                {filteredMedia.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          )}

          {filteredNews.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-newspaper-line text-primary-600" aria-hidden="true"></i>
                资讯结果（{filteredNews.length}）
              </h2>
              <ul className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {filteredNews.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/${item.id}`}
                      className="group flex items-start gap-4 p-4 transition-colors hover:bg-background-100 md:p-5"
                    >
                      <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-20 md:w-32">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 麻豆精品`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-xs font-medium text-accent-700">{item.category}</span>
                        <h3 className="mt-1 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 line-clamp-2 hidden text-xs text-foreground-500 sm:block">
                          {item.summary}
                        </p>
                        <p className="mt-1.5 text-xs text-foreground-400">
                          {formatDate(item.publishedAt)}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

    </div>
  );
}